import { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Briefcase, ExternalLink, AlertTriangle, ShieldCheck, Search, TrendingDown, Waves } from "lucide-react";
import api from "../lib/api";
import { formatEur, formatNumber } from "../lib/format";
import { Link } from "react-router-dom";

const REC_STYLE = {
    "Strong Buy Candidate": { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: ShieldCheck },
    "Bid Low": { color: "bg-teal-500/15 text-teal-300 border-teal-500/30", icon: TrendingDown },
    "Watch": { color: "bg-blue-500/15 text-blue-300 border-blue-500/30", icon: Search },
    "Needs Due Diligence": { color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: AlertTriangle },
    "Avoid": { color: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: AlertTriangle },
    "Data Insufficient": { color: "bg-muted text-muted-foreground border-border/60", icon: AlertTriangle },
};

const TABS = [
    { key: "top_buy_candidates", label: "Top Buy Candidates", icon: ShieldCheck },
    { key: "strong_buys", label: "Strong Buys", icon: ShieldCheck },
    { key: "under_market", label: "Under-Market", icon: TrendingDown },
    { key: "sea_view_picks", label: "Sea-View Picks", icon: Waves },
    { key: "price_reductions", label: "Price Reductions", icon: TrendingDown },
    { key: "needs_review", label: "Needs Review", icon: AlertTriangle },
    { key: "avoid", label: "Avoid", icon: AlertTriangle },
];

const InvestmentCommittee = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/investment-committee?limit=10");
                setData(data);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-12 w-1/2" />
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-8" data-testid="committee-page">
            <header>
                <h1 className="text-3xl sm:text-4xl font-serif flex items-center gap-3">
                    <Briefcase className="w-7 h-7 text-accent" /> Investment Committee
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Top deals ranked from real listings only. Each recommendation declares
                    its confidence, missing data, and the rule that produced the label.
                    No fabricated certainty.
                </p>
            </header>

            <Tabs defaultValue="top_buy_candidates" className="w-full">
                <TabsList className="flex flex-wrap h-auto">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <TabsTrigger
                                key={t.key}
                                value={t.key}
                                className="flex items-center gap-2"
                                data-testid={`tab-${t.key}`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {t.label}
                                <Badge variant="outline" className="ml-1 text-[10px] px-1.5">
                                    {data[t.key]?.length || 0}
                                </Badge>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {TABS.map((t) => (
                    <TabsContent key={t.key} value={t.key} className="mt-4">
                        <CandidateList items={data[t.key] || []} testid={`candidates-${t.key}`} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
};

const CandidateList = ({ items, testid }) => {
    if (!items.length) {
        return (
            <Card className="p-8 text-center border-dashed" data-testid={`${testid}-empty`}>
                <AlertTriangle className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                    No real listings meet this bucket's criteria yet. As soon as new data
                    is scraped that fits, it will appear here.
                </p>
            </Card>
        );
    }
    return (
        <div className="grid lg:grid-cols-2 gap-4" data-testid={testid}>
            {items.map((c) => <CandidateCard key={c.id} c={c} />)}
        </div>
    );
};

const CandidateCard = ({ c }) => {
    const style = REC_STYLE[c.recommendation] || REC_STYLE["Data Insufficient"];
    const RIcon = style.icon;
    return (
        <Card className="p-5 hover:border-accent/40 transition-colors" data-testid={`candidate-${c.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-wider mb-2 ${style.color}`}>
                        <RIcon className="w-3 h-3" />
                        {c.recommendation}
                        <span className="opacity-70">· conf {Math.round((c.confidence || 0) * 100)}%</span>
                    </div>
                    <Link
                        to={`/deals/${c.id}`}
                        className="block text-sm font-medium hover:text-accent line-clamp-2"
                        data-testid={`candidate-title-${c.id}`}
                    >
                        {c.title || "Untitled listing"}
                    </Link>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        {[c.municipality, c.neighbourhood].filter(Boolean).join(" · ") || "Location not available"}
                        {c.provenance_source && <span className="ml-2 opacity-60">via {c.provenance_source}</span>}
                    </div>
                </div>
                {c.original_url && (
                    <a
                        href={c.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-accent flex-shrink-0"
                        aria-label="Open original listing"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                )}
            </div>

            <div className="grid grid-cols-4 gap-2 mb-3 text-[11px]">
                <KeyVal label="Price" value={c.asking_price_eur != null ? formatEur(c.asking_price_eur) : <Insufficient />} />
                <KeyVal
                    label={c.property_category === "land" ? "Plot" : "Area"}
                    value={
                        c.property_category === "land"
                            ? (c.plot_area_sqm != null ? `${formatNumber(c.plot_area_sqm, 0)} m²` : (c.living_area_sqm != null ? `${formatNumber(c.living_area_sqm, 0)} m²` : <Insufficient />))
                            : (c.living_area_sqm != null ? `${formatNumber(c.living_area_sqm, 0)} m²` : <Insufficient />)
                    }
                />
                <KeyVal label="€/m²" value={c.price_per_sqm_eur != null ? `€${formatNumber(c.price_per_sqm_eur, 0)}` : <Insufficient />} />
                <KeyVal
                    label="Discount"
                    value={c.discount_to_median_pct != null
                        ? <span className={c.discount_to_median_pct > 0 ? "text-emerald-400" : "text-rose-400"}>
                            {c.discount_to_median_pct > 0 ? "−" : "+"}
                            {Math.abs(c.discount_to_median_pct).toFixed(1)}%
                          </span>
                        : <Insufficient />}
                />
                <KeyVal label="Flip score" value={c.flip_score != null ? formatNumber(c.flip_score, 0) : <Insufficient />} />
                <KeyVal label="Sea view" value={c.sea_view && c.sea_view !== "unknown" ? c.sea_view : <span className="text-muted-foreground">unknown</span>} />
                <KeyVal label="Category" value={c.property_category || <Insufficient />} />
                <KeyVal label="Area median" value={c.area_median_price_per_sqm != null ? `€${formatNumber(c.area_median_price_per_sqm, 0)}/m²` : <Insufficient />} />
            </div>

            {(c.data_quality_flags?.length > 0 || c.missing_data?.length > 0) && (
                <div className="mb-3 space-y-1.5 text-[11px]">
                    {c.data_quality_flags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid={`candidate-flags-${c.id}`}>
                            {c.data_quality_flags.map((f) => (
                                <Badge key={f} variant="outline" className="text-[9px] border-amber-500/30 text-amber-400">
                                    {f}
                                </Badge>
                            ))}
                        </div>
                    )}
                    {c.missing_data?.length > 0 && (
                        <div className="text-muted-foreground">
                            <span className="opacity-70">Missing:</span> {c.missing_data.join(", ")}
                        </div>
                    )}
                </div>
            )}

            <p className="text-[12px] text-foreground/80 italic border-l-2 border-accent/40 pl-3">
                {c.reason}
            </p>
        </Card>
    );
};

const KeyVal = ({ label, value }) => (
    <div>
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-foreground">{value}</div>
    </div>
);

const Insufficient = () => <span className="text-muted-foreground italic text-[10px]">Not available</span>;

export default InvestmentCommittee;
